import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, betreuerByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { ServicePosition, defaultServicePosition, moveUpServicePosition } from 'api/servicePosition';
import { EditIcon, DeleteIcon, UpIcon, RepeatAllIcon, RepeatOneIcon } from '@fluentui/react-icons';
import { Kunde } from 'api/kunde';
import { formStyles } from 'styles/formStyles';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { getArtikelDetailId } from 'utils/helper';
import { Service } from 'api/service';
import { Termin } from 'api/termin';
import { authSelector } from 'store/auth/selector';
import { TerminEdit, TerminEditData } from 'components/Termin/TerminEdit';
import { ServicePositionEdit } from './ServicePositionEdit';
import { ServicePositionDelete } from './ServicePositionDelete';
import { ServicePositionenHeader } from './ServicePositionenHeader';

interface ServicePositionenProps {
    kunde: Kunde;
    service: Service;
    sNr: string;
    servicePositionen: ServicePosition[];
    wiedervorlagen: Termin[];
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    allowEdit?: boolean;
    refetch: () => void;
}

export const ServicePositionen: React.FC<ServicePositionenProps> = ({
    kunde,
    service,
    sNr,
    servicePositionen,
    wiedervorlagen,
    viewMode,
    withHeader = true,
    allowEdit = false,
    refetch,
}) => {
    const auth = useSelector(authSelector);
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);

    const [servicePositionEditData, setServicePositionEditData] = React.useState<ServicePosition | null>(null);
    const [servicePositionDeleteData, setServicePositionDeleteData] = React.useState<ServicePosition | null>(null);
    const [terminEditData, setTerminEditData] = React.useState<TerminEditData | null>(null);

    return (
        <>
            <table className={formStyles.positionenTable}>
                {withHeader && (
                    <ServicePositionenHeader
                        allowEdit={allowEdit}
                        onAdd={() =>
                            setServicePositionEditData({
                                ...defaultServicePosition,
                                sNr,
                                pos: -1,
                            })
                        }
                    />
                )}
                <tbody>
                    {/* hide Reclay lines in short view */}
                    {servicePositionen
                        .filter((servicePosition) => viewMode === 'long' || servicePosition.artikelGruppeNr !== 97)
                        .map((servicePosition) => {
                            const wiedervorlage = (wiedervorlagen || []).find(
                                (wv) =>
                                    wv.kNr === kunde.nr &&
                                    wv.artikelGruppeNr === servicePosition.artikelGruppeNr &&
                                    wv.artikelNr === servicePosition.artikelNr,
                            );

                            return (
                                <tr key={servicePosition.pos} className={formStyles.positionenRow}>
                                    <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                        {servicePosition.pos}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenArtikelGruppeCell,
                                        )}
                                    >
                                        {(artikelGruppenByNr[servicePosition.artikelGruppeNr] &&
                                            artikelGruppenByNr[servicePosition.artikelGruppeNr]?.text) ||
                                            servicePosition.artikelGruppeNr}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenArtikelNrCell,
                                        )}
                                    >
                                        <span>
                                            {servicePosition.artikelGruppeNr && servicePosition.artikelNr ? (
                                                <OpenLink
                                                    link={createRecordUrl(
                                                        'lager/artikel',
                                                        undefined,
                                                        getArtikelDetailId(
                                                            servicePosition.artikelGruppeNr,
                                                            servicePosition.artikelNr,
                                                        ),
                                                        undefined,
                                                        false,
                                                    )}
                                                    label={servicePosition.artikelNr}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    </td>
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}
                                    >
                                        {servicePosition.posText}
                                        {servicePosition.posBem ? (
                                            <i>
                                                <br />
                                                {servicePosition.posBem}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}
                                    >
                                        {servicePosition.posEinh
                                            ? einheitenByCode[servicePosition.posEinh].text || servicePosition.posEinh
                                            : '?'}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMengeCell,
                                        )}
                                    >
                                        {formatDecimal(servicePosition.posMenge, 3)}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                    >
                                        {formatMoney(servicePosition.posEPreis, 3)}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                        title={'VK-Brutto: ' + formatMoney(servicePosition.posBrutto)}
                                    >
                                        {formatMoney(servicePosition.posNetto)}
                                    </td>
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenMwstCell)}
                                        title={'VK-Brutto: ' + formatMoney(servicePosition.posBrutto)}
                                    >
                                        {formatPercent(servicePosition.posMwst)}
                                    </td>
                                    {(auth.rolle === 'GF' || auth.rolle === 'ID') && (
                                        <td
                                            className={classnames(
                                                formStyles.positionenCell,
                                                formStyles.positionenActionCell,
                                            )}
                                        >
                                            {servicePosition.artikelGruppeNr && servicePosition.artikelNr ? (
                                                wiedervorlage ? (
                                                    <RepeatOneIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={() => {
                                                            setTerminEditData({
                                                                kNr: wiedervorlage.kNr,
                                                                zfKaNr: wiedervorlage.zfKaNr || 0,
                                                            });
                                                        }}
                                                    />
                                                ) : (
                                                    <RepeatAllIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={() => {
                                                            setTerminEditData({
                                                                kNr: kunde.nr,
                                                                zfKaNr: -1,
                                                                initUserKz: service.telBetreuer
                                                                    ? betreuerByNr[service.telBetreuer]?.kz || auth.kz
                                                                    : auth.kz,
                                                                initKaNr: service.kaNr,
                                                                initKaName: service.kaName,
                                                                initTerminArt: 'W',
                                                                initTerminBetreff:
                                                                    (artikelGruppenByNr[servicePosition.artikelGruppeNr]
                                                                        ?.text || '?') +
                                                                    ' | ' +
                                                                    servicePosition.artikelNr,
                                                                initArtikelGruppeNr: servicePosition.artikelGruppeNr,
                                                                initArtikelNr: servicePosition.artikelNr,
                                                                initPosText: servicePosition.posText,
                                                                initPosEinh: servicePosition.posEinh,
                                                                initPosMenge: servicePosition.posMenge,
                                                            });
                                                        }}
                                                    />
                                                )
                                            ) : null}
                                        </td>
                                    )}

                                    {allowEdit && (
                                        <>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                {servicePosition.pos > 1 && (
                                                    <UpIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={async () => {
                                                            await moveUpServicePosition(
                                                                servicePosition.sNr,
                                                                servicePosition.pos,
                                                            );
                                                            if (refetch) {
                                                                refetch();
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </td>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                <EditIcon
                                                    className={formStyles.positionenAction}
                                                    onClick={() => setServicePositionEditData(servicePosition)}
                                                />
                                            </td>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                <DeleteIcon
                                                    className={formStyles.positionenAction}
                                                    onClick={() => setServicePositionDeleteData(servicePosition)}
                                                />
                                            </td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                </tbody>
            </table>
            {servicePositionEditData !== null && (
                <ServicePositionEdit
                    kunde={kunde}
                    sNr={servicePositionEditData.sNr}
                    pos={servicePositionEditData.pos}
                    onClose={(_refetch) => {
                        setServicePositionEditData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {servicePositionDeleteData !== null && (
                <ServicePositionDelete
                    servicePosition={servicePositionDeleteData}
                    onClose={(_refetch) => {
                        setServicePositionDeleteData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {terminEditData !== null && (
                <TerminEdit
                    defaultKNr={terminEditData.kNr}
                    zfKaNr={terminEditData.zfKaNr}
                    initUserKz={terminEditData.initUserKz}
                    initKaNr={terminEditData.initKaNr}
                    initTerminArt={terminEditData.initTerminArt}
                    initTerminBetreff={terminEditData.initTerminBetreff}
                    initArtikelGruppeNr={terminEditData.initArtikelGruppeNr}
                    initArtikelNr={terminEditData.initArtikelNr}
                    initPosText={terminEditData.initPosText}
                    initPosEinh={terminEditData.initPosEinh}
                    initPosMenge={terminEditData.initPosMenge}
                    onClose={(doRefetch) => {
                        setTerminEditData(null);
                        if (doRefetch) {
                            refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
